/**
 * Admin CRUD for 100 Canales Dijeron surveys.
 * - Correct answers (3–10) shown on the board.
 * - Decoys (0–10) are hidden traps; a match in live-duel mode = instant strike.
 * - AI suggest via Gemini autopoblates the top-N correct answers.
 */
import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Trophy, Plus, X, Save, Trash2, Sparkles, Loader2, Play, Edit,
  ToggleRight, ToggleLeft, ShieldAlert, ArrowUp, ArrowDown,
} from "lucide-react";

import { api } from "@/api/client";
import { useLang } from "@/context/LangContext";
import { Input } from "@/components/ui/input";

const suggestedPoints = (i, total) => {
  // Rough Family-Feud style distribution.
  const scale = [40, 25, 15, 10, 5, 3, 2, 1, 1, 1];
  if (i < scale.length) return scale[i];
  return Math.max(1, 40 - i * 4);
};

const emptySurvey = () => ({
  slug: "",
  question_es: "",
  question_en: "",
  category: "objeciones",
  time_seconds: 90,
  active: true,
  answers: Array.from({ length: 5 }, (_, i) => ({
    rank: i + 1,
    text: "",
    text_en: "",
    points: suggestedPoints(i, 5),
    keywords: [],
  })),
  decoys: [],
});

export default function DijeronAdmin() {
  const { lang } = useLang();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptySurvey());
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/dijeron/surveys");
      setItems(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptySurvey());
    setModalOpen(true);
  };
  const openEdit = (s) => {
    setEditing(s);
    setForm({
      slug: s.slug,
      question_es: s.question_es,
      question_en: s.question_en || "",
      category: s.category || "objeciones",
      time_seconds: s.time_seconds || 90,
      active: s.active !== false,
      answers: (s.answers || []).map((a, i) => ({
        rank: a.rank ?? i + 1,
        text: a.text || "",
        text_en: a.text_en || "",
        points: a.points ?? suggestedPoints(i, (s.answers || []).length),
        keywords: a.keywords || [],
      })),
      decoys: (s.decoys || []).map((d) => ({
        text: d.text || "",
        text_en: d.text_en || "",
        keywords: d.keywords || [],
      })),
    });
    setModalOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.question_es.trim()) {
      toast.error("Pregunta requerida");
      return;
    }
    if (form.answers.length < 2) {
      toast.error("Mínimo 2 respuestas correctas");
      return;
    }
    if (form.answers.some((a) => !a.text.trim())) {
      toast.error("Todas las respuestas correctas necesitan texto");
      return;
    }
    if (form.decoys.some((d) => !d.text.trim())) {
      toast.error("Los señuelos necesitan texto (o bórralos)");
      return;
    }
    // Renumber ranks 1..N before sending to avoid holes.
    const payload = {
      ...form,
      answers: form.answers.map((a, i) => ({ ...a, rank: i + 1 })),
    };
    setBusy(true);
    try {
      if (editing) {
        await api.put(`/admin/dijeron/surveys/${editing.survey_id}`, payload);
        toast.success("Guardado");
      } else {
        await api.post("/admin/dijeron/surveys", payload);
        toast.success("Encuesta creada");
      }
      setModalOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`¿Borrar "${s.question_es}"? También se borran ${s.play_count} juegos.`)) return;
    try {
      await api.delete(`/admin/dijeron/surveys/${s.survey_id}`);
      toast.success("Borrada");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const toggleActive = async (s) => {
    try {
      await api.put(`/admin/dijeron/surveys/${s.survey_id}`, { ...s, active: !s.active });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const aiSuggest = async () => {
    if (!form.question_es.trim()) {
      toast.error("Escribe la pregunta primero");
      return;
    }
    setAiBusy(true);
    try {
      const { data } = await api.post("/admin/dijeron/ai-suggest", {
        question: form.question_es,
        n_answers: Math.max(3, form.answers.length),
      });
      if (data.answers?.length) {
        setForm((f) => ({
          ...f,
          answers: data.answers.map((a, i) => ({
            rank: a.rank || (i + 1),
            text: (a.text || "").toUpperCase(),
            text_en: "",
            points: a.points || suggestedPoints(i, data.answers.length),
            keywords: a.keywords || [],
          })),
        }));
        toast.success(`Gemini sugirió ${data.answers.length} respuestas — revisa y ajusta`);
      } else {
        toast.error("Gemini no devolvió respuestas");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI error");
    } finally {
      setAiBusy(false);
    }
  };

  const updateAnswer = (i, patch) => {
    setForm((f) => {
      const answers = [...f.answers];
      answers[i] = { ...answers[i], ...patch };
      return { ...f, answers };
    });
  };
  const addAnswer = () => setForm((f) => {
    if (f.answers.length >= 10) return f;
    const nextRank = f.answers.length + 1;
    return {
      ...f,
      answers: [...f.answers, { rank: nextRank, text: "", text_en: "", points: suggestedPoints(nextRank - 1, nextRank), keywords: [] }],
    };
  });
  const removeAnswer = (i) => setForm((f) => ({
    ...f, answers: f.answers.filter((_, idx) => idx !== i),
  }));
  const moveAnswer = (i, dir) => setForm((f) => {
    const answers = [...f.answers];
    const j = i + dir;
    if (j < 0 || j >= answers.length) return f;
    [answers[i], answers[j]] = [answers[j], answers[i]];
    return { ...f, answers };
  });

  const updateDecoy = (i, patch) => setForm((f) => {
    const decoys = [...f.decoys];
    decoys[i] = { ...decoys[i], ...patch };
    return { ...f, decoys };
  });
  const addDecoy = () => setForm((f) => {
    if (f.decoys.length >= 10) return f;
    return { ...f, decoys: [...f.decoys, { text: "", text_en: "", keywords: [] }] };
  });
  const removeDecoy = (i) => setForm((f) => ({
    ...f, decoys: f.decoys.filter((_, idx) => idx !== i),
  }));

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      className="space-y-5" data-testid="admin-dijeron-page"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display font-black text-4xl flex items-center gap-3">
            <Sparkles className="w-8 h-8 text-canales-red" />
            100 Canales Dijeron
          </h1>
          <p className="font-body text-neutral-700 mt-1">
            Crea encuestas estilo Family Feud. Respuestas correctas + señuelos ocultos que fuerzan strike en Duelo LIVE.
          </p>
        </div>
        <div className="flex gap-2">
          <a
            href="/juego/canales-dijeron"
            target="_blank"
            rel="noreferrer"
            className="brutal-btn bg-white px-3 py-2 inline-flex items-center gap-1 text-sm"
          >
            <Play className="w-4 h-4" /> Ver el juego
          </a>
          <button
            onClick={openCreate}
            data-testid="dijeron-new-btn"
            className="brutal-btn bg-canales-red text-white px-4 py-2 font-display font-black inline-flex items-center gap-1"
          >
            <Plus className="w-4 h-4" /> Nueva encuesta
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin inline-block" /></div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {items.map((s) => (
            <div key={s.survey_id} className="brutal bg-white p-4 space-y-2" data-testid={`dijeron-row-${s.slug}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] font-display font-black uppercase tracking-widest text-canales-red">
                    {s.category} · {s.time_seconds}s · {s.play_count || 0} juegos · {(s.decoys || []).length} señuelos
                  </div>
                  <div className="font-display font-black text-lg leading-tight mt-1">
                    {s.question_es}
                  </div>
                </div>
                <button
                  onClick={() => toggleActive(s)}
                  title={s.active ? "Desactivar" : "Activar"}
                  className="shrink-0"
                >
                  {s.active
                    ? <ToggleRight className="w-8 h-8 text-green-700" />
                    : <ToggleLeft className="w-8 h-8 text-neutral-400" />}
                </button>
              </div>
              <ol className="space-y-0.5 text-xs">
                {(s.answers || []).map((a) => (
                  <li key={a.rank} className="flex items-center justify-between">
                    <span className="truncate">
                      <b className="text-canales-red">#{a.rank}</b> {a.text}
                    </span>
                    <span className="font-mono text-neutral-500">{a.points}</span>
                  </li>
                ))}
              </ol>
              {(s.decoys || []).length > 0 && (
                <div className="pt-1 border-t border-dashed border-neutral-300">
                  <div className="text-[10px] font-display font-black uppercase tracking-widest text-orange-600 flex items-center gap-1">
                    <ShieldAlert className="w-3 h-3" /> Señuelos ({(s.decoys || []).length})
                  </div>
                  <div className="text-[11px] text-neutral-600 truncate">
                    {(s.decoys || []).map((d) => d.text).join(" · ")}
                  </div>
                </div>
              )}
              <div className="flex gap-1 pt-1">
                <button
                  onClick={() => openEdit(s)}
                  data-testid={`dijeron-edit-${s.slug}`}
                  className="brutal-sm bg-canales-blue text-white text-xs px-3 py-1 font-display font-bold inline-flex items-center gap-1"
                >
                  <Edit className="w-3 h-3" /> Editar
                </button>
                <a
                  href={`/juego/canales-dijeron/${s.slug}`}
                  target="_blank" rel="noreferrer"
                  className="brutal-sm bg-canales-yellow text-black text-xs px-3 py-1 font-display font-bold inline-flex items-center gap-1"
                >
                  <Play className="w-3 h-3" /> Probar
                </a>
                <button
                  onClick={() => remove(s)}
                  data-testid={`dijeron-delete-${s.slug}`}
                  className="brutal-sm bg-canales-red text-white text-xs px-3 py-1 font-display font-bold inline-flex items-center gap-1 ml-auto"
                >
                  <Trash2 className="w-3 h-3" /> Borrar
                </button>
              </div>
            </div>
          ))}
          {items.length === 0 && (
            <div className="col-span-2 brutal bg-white p-8 text-center text-neutral-500">
              Aún no hay encuestas. Clic en "Nueva encuesta".
            </div>
          )}
        </div>
      )}

      {/* Modal */}
      {modalOpen && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50"
          onClick={() => !busy && setModalOpen(false)}
        >
          <div
            className="brutal bg-white w-full max-w-3xl max-h-[92vh] overflow-y-auto p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display font-black text-2xl">
                {editing ? "Editar encuesta" : "Nueva encuesta"}
              </h2>
              <button onClick={() => setModalOpen(false)}><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={submit} className="space-y-4">
              <label className="block">
                <span className="text-xs font-display font-black uppercase tracking-widest text-canales-red">
                  Pregunta (estilo "encuestamos a 100 vendedores…")
                </span>
                <Input
                  data-testid="dijeron-form-question"
                  value={form.question_es}
                  onChange={(e) => setForm((f) => ({ ...f, question_es: e.target.value }))}
                  placeholder="¿Cuál es la objeción #1 que oyes en piso?"
                  className="brutal-sm mt-1 py-5"
                  autoFocus
                />
              </label>
              <label className="block">
                <span className="text-xs font-display font-black uppercase tracking-widest text-canales-blue">
                  Pregunta (Inglés — opcional)
                </span>
                <Input
                  value={form.question_en}
                  onChange={(e) => setForm((f) => ({ ...f, question_en: e.target.value }))}
                  placeholder="What is the #1 objection you hear on the floor?"
                  className="brutal-sm mt-1"
                />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-xs font-display font-black uppercase tracking-widest text-canales-red">Categoría</span>
                  <Input
                    value={form.category}
                    onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                    className="brutal-sm mt-1"
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-display font-black uppercase tracking-widest text-canales-red">Segundos</span>
                  <Input
                    type="number" min="30" max="300"
                    value={form.time_seconds}
                    onChange={(e) => setForm((f) => ({ ...f, time_seconds: parseInt(e.target.value, 10) || 90 }))}
                    className="brutal-sm mt-1"
                  />
                </label>
              </div>

              <button
                type="button"
                onClick={aiSuggest}
                disabled={aiBusy || !form.question_es.trim()}
                data-testid="dijeron-ai-suggest"
                className="w-full brutal-btn bg-canales-yellow text-black px-4 py-3 inline-flex items-center justify-center gap-2 font-display font-black disabled:opacity-60"
              >
                {aiBusy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Sparkles className="w-5 h-5" />}
                Sugerir respuestas con Gemini AI
              </button>

              {/* Correct answers */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="text-xs font-display font-black uppercase tracking-widest text-canales-red">
                    Respuestas correctas ({form.answers.length}/10) — visibles en el tablero
                  </div>
                  <button
                    type="button"
                    onClick={addAnswer}
                    disabled={form.answers.length >= 10}
                    data-testid="dijeron-add-answer"
                    className="brutal-sm bg-canales-red text-white text-xs px-3 py-1 font-display font-bold inline-flex items-center gap-1 disabled:opacity-50"
                  >
                    <Plus className="w-3 h-3" /> Agregar
                  </button>
                </div>
                {form.answers.map((a, i) => (
                  <div key={i} className="brutal-sm p-3 bg-canales-cream space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="font-display font-black text-xl text-canales-red w-8">#{i + 1}</span>
                      <Input
                        value={a.text}
                        onChange={(e) => updateAnswer(i, { text: e.target.value.toUpperCase() })}
                        placeholder="ESTÁ MUY CARO"
                        className="brutal-sm flex-1"
                      />
                      <Input
                        type="number" min="0" max="100"
                        value={a.points}
                        onChange={(e) => updateAnswer(i, { points: parseInt(e.target.value, 10) || 0 })}
                        className="brutal-sm w-20"
                        title="Puntos"
                      />
                      <div className="flex flex-col">
                        <button type="button" onClick={() => moveAnswer(i, -1)} disabled={i === 0} className="text-neutral-500 hover:text-black disabled:opacity-30" title="Subir">
                          <ArrowUp className="w-4 h-4" />
                        </button>
                        <button type="button" onClick={() => moveAnswer(i, 1)} disabled={i === form.answers.length - 1} className="text-neutral-500 hover:text-black disabled:opacity-30" title="Bajar">
                          <ArrowDown className="w-4 h-4" />
                        </button>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeAnswer(i)}
                        disabled={form.answers.length <= 2}
                        className="text-canales-red hover:text-black disabled:opacity-30"
                        title="Eliminar"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <Input
                      value={a.text_en || ""}
                      onChange={(e) => updateAnswer(i, { text_en: e.target.value.toUpperCase() })}
                      placeholder="🇺🇸 English translation (optional)"
                      className="brutal-sm text-xs"
                    />
                    <Input
                      value={(a.keywords || []).join(", ")}
                      onChange={(e) => updateAnswer(i, { keywords: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                      placeholder="palabras clave separadas por coma (fuzzy match, ES+EN)"
                      className="brutal-sm text-xs"
                    />
                  </div>
                ))}
              </div>

              {/* Decoys */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="text-xs font-display font-black uppercase tracking-widest text-orange-600 flex items-center gap-1">
                    <ShieldAlert className="w-4 h-4" /> Señuelos ({form.decoys.length}/10) — NO en el tablero · dan strike
                  </div>
                  <button
                    type="button"
                    onClick={addDecoy}
                    disabled={form.decoys.length >= 10}
                    data-testid="dijeron-add-decoy"
                    className="brutal-sm bg-orange-500 text-white text-xs px-3 py-1 font-display font-bold inline-flex items-center gap-1 disabled:opacity-50"
                  >
                    <Plus className="w-3 h-3" /> Agregar señuelo
                  </button>
                </div>
                {form.decoys.length === 0 && (
                  <div className="text-xs text-neutral-500 italic">
                    Sin señuelos aún. Agrega respuestas plausibles pero incorrectas que quieres castigar en LIVE
                    (p.ej. "los créditos" cuando la pregunta era sobre productos).
                  </div>
                )}
                {form.decoys.map((d, i) => (
                  <div key={i} className="brutal-sm p-3 bg-orange-50 border-orange-500 space-y-2">
                    <div className="flex items-center gap-2">
                      <ShieldAlert className="w-5 h-5 text-orange-600 shrink-0" />
                      <Input
                        value={d.text}
                        onChange={(e) => updateDecoy(i, { text: e.target.value.toUpperCase() })}
                        placeholder="RESPUESTA INCORRECTA PERO COMÚN"
                        className="brutal-sm flex-1"
                      />
                      <button
                        type="button"
                        onClick={() => removeDecoy(i)}
                        className="text-canales-red hover:text-black"
                        title="Eliminar señuelo"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <Input
                      value={d.text_en || ""}
                      onChange={(e) => updateDecoy(i, { text_en: e.target.value.toUpperCase() })}
                      placeholder="🇺🇸 English decoy (optional)"
                      className="brutal-sm text-xs"
                    />
                    <Input
                      value={(d.keywords || []).join(", ")}
                      onChange={(e) => updateDecoy(i, { keywords: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                      placeholder="palabras clave del señuelo (fuzzy match)"
                      className="brutal-sm text-xs"
                    />
                  </div>
                ))}
              </div>

              <label className="flex items-center gap-2 text-sm font-body">
                <input
                  type="checkbox"
                  checked={form.active}
                  onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
                  className="w-4 h-4"
                />
                <span>Activa (visible para vendedores)</span>
              </label>

              <div className="flex justify-end gap-2 pt-2 sticky bottom-0 bg-white pb-1">
                <button type="button" onClick={() => setModalOpen(false)} disabled={busy} className="brutal-btn bg-white px-4 py-2">
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={busy}
                  data-testid="dijeron-save"
                  className="brutal-btn bg-canales-red text-white px-6 py-2 inline-flex items-center gap-2 font-display font-black disabled:opacity-60"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Guardar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </motion.div>
  );
}
